import { createApi } from '@reduxjs/toolkit/query/react';
import apiEndpoints from 'api/endpoints';
import { API as sandbox } from 'config/sandbox';
import { calculateDateRange } from 'helper/utility';
import { PAGE_SIZE } from 'helper/values';

import { baseQueryWrapper } from './baseApi';

export const globalAPI = createApi({
    reducerPath: 'global-api',
    baseQuery: baseQueryWrapper,
    endpoints: (builder) => ({
        getCountyHistory: builder.query({
            query: (userAccessCode) => ({
                url: apiEndpoints.countyHistory,
                method: 'POST',
                body: { userAccessCode },
            }),
        }),
        searchCounties: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.searchCounties,
                method: 'POST',
                body: payload,
            }),
        }),
        submitExtendedChecks: builder.mutation({
            query: ({ payload, checkName }) => {
                const checkUrls = {
                    'county criminal': apiEndpoints.countyCriminal,
                    'county civil': apiEndpoints.countyCivil,
                    'federal criminal': apiEndpoints.federalCriminal,
                    'federal civil': apiEndpoints.federalCivil,
                };

                return {
                    url: checkUrls[checkName],
                    method: 'POST',
                    body: payload,
                };
            },
        }),
        getCaseReport: builder.query({
            query: (payload) => ({
                url: apiEndpoints.extendedCheckCaseReport,
                method: 'POST',
                body: payload,
            }),
        }),
        getStats: builder.query({
            query: ({ duration, formattedApiDates = null } = {}) => {
                let formattedStartDate, formattedEndDate;

                // If the caller provides dates, prefer them (works for CUSTOM and non-CUSTOM durations)
                if (formattedApiDates) {
                    [formattedStartDate, formattedEndDate] = formattedApiDates;
                } else {
                    const { formatStartDate, formatEndDate } = calculateDateRange(duration);
                    formattedStartDate = formatStartDate;
                    formattedEndDate = formatEndDate;
                }

                const startDate = new Date();
                formattedStartDate = formattedStartDate || startDate.toISOString();
                formattedEndDate =
                    formattedEndDate ||
                    new Date(new Date().setDate(new Date().getDate() - 7)).toISOString();

                const url = `${apiEndpoints.statistics}?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;

                return {
                    url,
                    method: 'GET',
                };
            },
        }),

        getLowCodeScriptSettings: builder.query({
            query: () => ({
                url: apiEndpoints.settings.lowCodeScripts.base,
                method: 'GET',
            }),
            providesTags: (result, error, { cac }) => [{ type: 'lowCodeScriptSettings', cac }],
        }),

        manageLowCodeScriptSettings: builder.mutation({
            query: ({ payload, type }) => {
                const url = apiEndpoints.settings.lowCodeScripts?.[type];

                const data = {
                    allowedDomains: payload?.allowedDomains,
                };

                if (payload?.maxLinksPerDay) {
                    data.maxLinksPerDay = payload?.maxLinksPerDay;
                }

                return {
                    url: url,
                    method: 'POST',
                    body: data,
                };
            },
            invalidatesTags: (result, error, { cac }) => [{ type: 'lowCodeScriptSettings', cac }],
        }),
        updateIntegrationSettings: builder.mutation({
            query: ({ key, ...rest }) => ({
                url: `${apiEndpoints.setting}/${key.id}/settings`,
                method: 'POST',
                body: rest,
            }),
        }),
        activateIntegration: builder.mutation({
            query: ({ key, ...rest }) => ({
                url: `${apiEndpoints.setting}/${key.id}/activate`,
                method: 'POST',
                body: rest,
            }),
        }),
        deactivateIntegration: builder.mutation({
            query: ({ key, ...rest }) => ({
                url: `${apiEndpoints.setting}/${key.id}/deactivate`,
                method: 'POST',
                body: rest,
            }),
        }),
        getBrandListings: builder.query({
            query: ({ brandUuid, integrationName, filter = '', page = 1, size = PAGE_SIZE }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/brand/${brandUuid}/listings?page=${page}&pageSize=${size}&filter=${filter}`,
                method: 'GET',
            }),
            providesTags: ['brandListings'],
        }),
        getListings: builder.query({
            query: ({ payload, page, size = PAGE_SIZE, integrationName }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/listings?page=${page}&pageSize=${size}`,
                method: 'POST',
                body: payload,
            }),
        }),
        moveListings: builder.mutation({
            query: ({ payload, integrationName }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/move/listings`,
                method: 'PATCH',
                body: payload,
            }),
            invalidatesTags: ['brandListings'],
        }),
        deleteBrand: builder.mutation({
            query: ({ brandUuid, integrationName }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/brand/${brandUuid}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['deleteBrand'],
        }),
        getBrand: builder.query({
            query: ({ brandUUID, integrationName }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/brand/${brandUUID}`,
                method: 'GET',
            }),
            providesTags: ['updateBrand'],
        }),
        getDefaultBrand: builder.query({
            query: ({ integrationName }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/brand`,
                method: 'GET',
            }),
        }),
        updateBrand: builder.mutation({
            query: ({ brandUUID, integrationName, payload }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/brand/${brandUUID}`,
                method: 'PUT',
                body: payload,
            }),
            invalidatesTags: ['updateBrand'],
        }),
        listBrands: builder.query({
            query: ({ page = 1, size = PAGE_SIZE, filter = '', integrationName }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/brands/?page=${page}&pageSize=${size}&filter=${filter}`,
                method: 'GET',
            }),
            providesTags: ['updateBrand', 'deleteBrand'],
        }),
        markDefault: builder.mutation({
            query: ({ brandUUID, integrationName }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/brand/${brandUUID}/default`,
                method: 'PATCH',
            }),
            invalidatesTags: ['updateBrand'],
        }),
        fetchAllWorkflow: builder.query({
            query: ({ page = 0, size = PAGE_SIZE }) => ({
                url: `${apiEndpoints.workflowUrl}?pageNumber=${page}&pageSize=${size}`,
                method: 'GET',
            }),
            providesTags: ['workflows'],
        }),
        fetchOneWorkflow: builder.query({
            query: (workflowId) => ({
                url: `${apiEndpoints.workflowUrl}/${workflowId}`,
                method: 'GET',
            }),
            providesTags: ['workflow'],
        }),
        createBrand: builder.mutation({
            query: ({ payload, integrationName }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/brand`,
                method: 'POST',
                body: payload,
            }),
        }),
        getIntegrationSettings: builder.query({
            query: (id) => ({
                url: `${apiEndpoints.setting}/${id}/settings`,
                method: 'GET',
            }),
        }),
        exchangeToken: builder.mutation({
            query: ({ payload, integrationName }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/exchange/token`,
                method: 'POST',
                body: payload,
            }),
        }),
        updateListings: builder.mutation({
            query: ({ payload, integrationName }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/listings/enabled`,
                method: 'PATCH',
                body: payload,
            }),
        }),
        getSyncListingsStatus: builder.query({
            query: ({ integrationName }) => ({
                url: `${apiEndpoints.integrationBase}/${integrationName}/sync-listing/status`,
                method: 'GET',
            }),
        }),

        getBookingChannels: builder.query({
            query: (integrationId) => ({
                url: `${apiEndpoints.setting}/${integrationId}${apiEndpoints.integrationSettingValues}`,
                method: 'GET',
            }),
        }),
        updateBookingChannels: builder.mutation({
            query: ({ integrationId, payload }) => ({
                url: `${apiEndpoints.setting}/${integrationId}${apiEndpoints.integrationSettingValues}`,
                method: 'PUT',
                body: payload,
            }),
        }),

        sandboxAutoLogin: builder.mutation({
            query: (payload) => ({
                url: apiEndpoints.sandboxLogin,
                method: 'POST',
                body: payload,
            }),
        }),

        fetchSandboxSession: builder.mutation({
            query: ({ token }) => ({
                baseUrl: sandbox.BASE_URL,
                url: `${apiEndpoints.sandboxSession}`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
        }),
        getSetupGuideStatus: builder.mutation({
            query: (params) => ({
                url: apiEndpoints.setupGuideStatus,
                method: 'GET',
                params,
            }),
        }),
        // Admin terms/consent uses the pre-login bearer handoff token (not the
        // authenticated secret_token), so the header is overridden per request.
        submitConsent: builder.mutation({
            query: ({ consent, token }) => ({
                url: apiEndpoints.terms,
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: consent,
            }),
        }),
    }),
});

export const {
    useGetCountyHistoryQuery: useCountyHistory,
    useLazyGetCountyHistoryQuery: useLazyCountyHistory,
    useSearchCountiesMutation: useSearchCounties,
    useSubmitExtendedChecksMutation: useSubmitCheck,
    useGetCaseReportQuery: useCaseReport,
    useGetStatsQuery: useStats,
    useManageLowCodeScriptSettingsMutation: useManageLowCodeScriptSettings,
    useGetLowCodeScriptSettingsQuery: useGetLowCodeScriptSettings,
    useUpdateIntegrationSettingsMutation: useUpdateIntegrationSettings,
    useActivateIntegrationMutation: useActivateIntegration,
    useDeactivateIntegrationMutation: useDeactivateIntegration,
    useExchangeTokenMutation: useExchangeToken,
    useSandboxAutoLoginMutation: useSandboxAutoLogin,
    useFetchSandboxSessionMutation: useFetchSandboxSession,
    useGetSetupGuideStatusMutation: useGetSetupGuideStatus,
    useSubmitConsentMutation: useSubmitConsent,
    useLazyGetBrandListingsQuery,
    useGetBrandListingsQuery,
    useGetListingsQuery,
    useLazyGetListingsQuery,
    useMoveListingsMutation,
    useDeleteBrandMutation,
    useLazyGetDefaultBrandQuery,
    useGetDefaultBrandQuery,
    useLazyGetBrandQuery,
    useUpdateBrandMutation,
    useLazyListBrandsQuery,
    useMarkDefaultMutation,
    useLazyFetchAllWorkflowQuery,
    useLazyFetchOneWorkflowQuery,
    useCreateBrandMutation,
    useGetIntegrationSettingsQuery,
    useUpdateListingsMutation,
    useGetSyncListingsStatusQuery,
    useLazyGetSyncListingsStatusQuery,
    useGetBookingChannelsQuery,
    useUpdateBookingChannelsMutation,
} = globalAPI;

export default globalAPI.middleware;
